import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Offer} from '../../../core/models/offer/offer.model';
import {OfferModificationsService} from '../../../core/services/impl/offer-modifications/offer-modifications.service';
import {OfferBasicItemComponent} from '../offer-basic-item/offer-basic-item.component';
import {LanguageService} from '../../services/language.service';
import {InstallmentStateService} from '../../../core/services/impl/section-services/installment/installment-state.service';
import {OfferResourceService} from '../../../core/services/impl/offer-resource.service';
import {StepKey} from '../../../core/enums/step-key.enum';
import {OffersTableService} from '../../../core/services/impl/offers-table.service';
import {Subscription} from 'rxjs/Rx';

@Component({
  selector: 'app-offer-summary',
  templateUrl: './offer-summary.component.html',
  styleUrls: []
})
export class OfferSummaryComponent extends OfferBasicItemComponent implements OnInit, OnDestroy {

  @Input() state: OfferSummaryState;
  @Input() stepKey: string;
  @Input() offer?: Offer;
  @Input() buttonDisabled: boolean = false;
  @Output() action: EventEmitter<OfferSummaryAction> = new EventEmitter<OfferSummaryAction>();

  priceLoading: boolean = false;
  lowestFirstInstallmentValue: number;

  protected recalculateButtonDisabled: boolean = false;
  private subscriptions: Subscription[] = [];

  get selectedOffer(): Offer {
    return this.offer ? this.offer : this.installmentStateService.selectedOffer;
  }

  get hasModifications(): boolean {
    if (this.stepKey === StepKey.SECOND_STEP) {
      return false;
    }
    return this.offerModificationsService.offerModificationsEnabled && this.selectedOffer && this.selectedOffer.modifications && this.selectedOffer.modifications.length > 0;
  }

  get buttonLabel(): string {
    return this.offerModificationsService.recalculatePremiumStepButtonVisible ? 'OFFER_MODIFICATIONS.recalculatePremium' : this.state.buttonLabel;
  }

  get isRecalculationMode(): boolean {
    return this.offerModificationsService.recalculatePremiumStepButtonVisible;
  }

  get firstInstallmentValue(): number | null {
    if (this.isRecalculationMode) {
      return this.offerModificationsService.offerCurrentPrice ? this.installmentStateService.firstInstallmentValue : null;
    }
    return this.installmentStateService.firstInstallmentValue;
  }

  get showFirstInstallmentValue(): boolean {
    return this.stepKey === StepKey.THIRD_STEP && !!this.installmentStateService.firstInstallmentValue;
  }

  get showLowestInstallmentValue(): boolean {
    return this.stepKey === StepKey.SECOND_STEP;
  }

  constructor(language: LanguageService,
              resourceService: OfferResourceService,
              public offerModificationsService: OfferModificationsService,
              public installmentStateService: InstallmentStateService,
              private offersTableService: OffersTableService) {
    super(resourceService, language);
  }

  ngOnInit() {
    this.init();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  pay(): void {
    const action = this.stepKey === StepKey.THIRD_STEP ? OfferSummaryAction.PAY : OfferSummaryAction.BUY_ONLINE;
    this.action.emit(action);
  }

  recalculatePremium() {
    if (!this.recalculateButtonDisabled) {
      this.action.emit(OfferSummaryAction.RECALCULATE_PREMIUM);
      this.handlePremiumRecalculation();
    }
  }

  private handlePremiumRecalculation(): void {
    this.priceLoading = true;
    this.recalculateButtonDisabled = true;
    const subscription = this.offerModificationsService.modifiedOfferEvent.take(1).subscribe(() => {
      this.priceLoading = false;
      this.recalculateButtonDisabled = false;
      subscription.unsubscribe();
    });

    const modifiedOfferWithErrorEventSubscription = this.offerModificationsService.modifiedOfferWithErrorEvent.take(1).subscribe(() => {
      this.priceLoading = false;
      this.recalculateButtonDisabled = false;
      modifiedOfferWithErrorEventSubscription.unsubscribe();
    });
  }

  private init() {
    this.setLogo(this.selectedOffer);
    this.createBoxes(this.selectedOffer);
    this.updateLowestInstallmentValue();
    this.subscriptions.push(
        this.offerModificationsService.modifiedOfferEvent.subscribe(() => this.createBoxes(this.selectedOffer))
    );
    this.subscriptions.push(
      this.offersTableService.offerSelected$.subscribe(() => {
        setTimeout(() => {
          this.setLogo(this.selectedOffer);
          this.createBoxes(this.selectedOffer);
          this.updateLowestInstallmentValue();
        }, 0);
      })
    )
  }

  private updateLowestInstallmentValue(): void {
    this.lowestFirstInstallmentValue = this.installmentStateService.getLowestFirstInstallmentValue(this.selectedOffer);
  }

}

export interface OfferSummaryState {
  buttonLabel: string;
}

export enum OfferSummaryAction {
  PAY = 'PAY',
  RECALCULATE_PREMIUM = 'RECALCULATE_PREMIUM',
  BUY_ONLINE = 'BUY_ONLINE',
}
